import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatLocationName = (loc) => `${loc.name}${loc.state ? `, ${loc.state}` : ""}, ${loc.country}`;

export function useDashboardLocationSearch({ user, updatePreferences, onLocationSelected }) {
  const [location, setLocation] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [savedLocations, setSavedLocations] = useState([]);

  const persistDefaultLocation = useCallback(async (defaultLocation) => {
    try {
      await axios.put(`${API}/user/preferences`, { default_location: defaultLocation });
      if (updatePreferences) updatePreferences({ default_location: defaultLocation });
    } catch (error) {
      console.error("Failed to save default location:", error);
    }
  }, [updatePreferences]);

  const applyLocation = useCallback((nextLocation, options = {}) => {
    setLocation(nextLocation);
    if (options.clearSearch) {
      setSearchQuery("");
      setSearchResults([]);
    }
    if (options.clearSummary && onLocationSelected) onLocationSelected();
  }, [onLocationSelected]);

  const fetchSavedLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setSavedLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch saved locations:", error);
    }
  }, []);

  const detectIpLocation = useCallback(async ({ toastId = "geo-detect" } = {}) => {
    const directServices = [
      async () => {
        const response = await fetch("https://ipapi.co/json/", { method: "GET", headers: { Accept: "application/json" } });
        if (!response.ok) return null;
        const data = await response.json();
        if (!data.latitude || !data.longitude || data.error) return null;
        return {
          lat: data.latitude,
          lon: data.longitude,
          name: `${data.city || "Unknown"}${data.region ? `, ${data.region}` : ""}${data.country_name ? `, ${data.country_name}` : ""}`,
          toastLabel: `${data.city}, ${data.region}`,
        };
      },
      async () => {
        const response = await fetch("https://ipinfo.io/json", { method: "GET", headers: { Accept: "application/json" } });
        if (!response.ok) return null;
        const data = await response.json();
        if (!data.loc) return null;
        const [lat, lon] = data.loc.split(",").map(Number);
        return {
          lat,
          lon,
          name: `${data.city || "Unknown"}${data.region ? `, ${data.region}` : ""}${data.country ? `, ${data.country}` : ""}`,
          toastLabel: `${data.city}, ${data.region}`,
        };
      },
    ];

    for (const service of directServices) {
      try {
        const detected = await service();
        if (detected) {
          toast.success(`Location detected: ${detected.toastLabel}`, { id: toastId });
          applyLocation({ lat: detected.lat, lon: detected.lon, name: detected.name });
          setGettingLocation(false);
          return true;
        }
      } catch (error) {
        console.error("IP geolocation service failed:", error.message);
      }
    }

    try {
      const response = await axios.get(`${API}/geolocate/ip`, { timeout: 8000 });
      if (response.data?.success && response.data.lat && response.data.lon) {
        const { lat, lon, city, region, country } = response.data;
        toast.success(`Location detected: ${city}, ${region}`, { id: toastId });
        applyLocation({ lat, lon, name: `${city || "Unknown"}${region ? `, ${region}` : ""}${country ? `, ${country}` : ""}` });
        setGettingLocation(false);
        return true;
      }
    } catch (error) {
      console.error("Backend geolocate/ip failed:", error.message);
    }

    return false;
  }, [applyLocation]);

  const fallbackToIPGeolocation = useCallback(async () => {
    const detected = await detectIpLocation({ toastId: "gps-request" });
    if (detected) return;

    try {
      const backendResponse = await axios.get(`${API}/user/ip-location`);
      if (backendResponse.data?.lat) {
        applyLocation({
          lat: backendResponse.data.lat,
          lon: backendResponse.data.lon,
          name: backendResponse.data.city || "Your Area",
        });
        toast.success(`Located: ${backendResponse.data.city || "Your Area"}`, { id: "gps-request" });
      } else {
        toast.error("Could not determine location. Please search manually.", { id: "gps-request" });
      }
    } catch (error) {
      console.error("IP geolocation fallback failed:", error);
      toast.error("Location unavailable. Please search for your city.", { id: "gps-request" });
    }
  }, [applyLocation, detectIpLocation]);

  const getUserLocation = useCallback(async () => {
    setGettingLocation(true);

    if (user?.preferences?.default_location) {
      const saved = user.preferences.default_location;
      toast.success(`Location: ${saved.name}`, { id: "geo-detect" });
      applyLocation({ lat: saved.lat, lon: saved.lon, name: saved.name });
      setGettingLocation(false);
      return;
    }

    toast.info("Detecting your location...", { id: "geo-detect", duration: 5000 });

    const fallbackToSavedOrManual = async () => {
      const ipSuccess = await detectIpLocation();
      if (ipSuccess) return;
      if (user?.preferences?.default_location) {
        const saved = user.preferences.default_location;
        toast.info(`Using saved location: ${saved.name}`, { id: "geo-detect" });
        applyLocation({ lat: saved.lat, lon: saved.lon, name: saved.name });
      } else {
        toast.error("Could not detect location", { id: "geo-detect" });
      }
      setGettingLocation(false);
    };

    if (!navigator.geolocation) {
      await fallbackToSavedOrManual();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        toast.success("GPS location acquired!", { id: "geo-detect" });

        let gpsLocation;
        try {
          const response = await axios.get(`${API}/weather/reverse`, { params: { lat: latitude, lon: longitude } });
          const locationName = response.data.name || "Your Location";
          const state = response.data.state ? `, ${response.data.state}` : "";
          const country = response.data.country ? `, ${response.data.country}` : "";
          gpsLocation = { lat: latitude, lon: longitude, name: `${locationName}${state}${country}` };
        } catch {
          gpsLocation = { lat: latitude, lon: longitude, name: "Your Location" };
        }
        applyLocation(gpsLocation);
        setGettingLocation(false);
        await persistDefaultLocation(gpsLocation);
      },
      async () => {
        toast.info("GPS unavailable, using IP detection...", { id: "geo-detect" });
        await fallbackToSavedOrManual();
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  }, [applyLocation, detectIpLocation, persistDefaultLocation, user]);

  const requestGPSLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      toast.error("GPS not supported by your browser");
      return;
    }

    toast.loading("Requesting GPS access...", { id: "gps-request" });

    if (navigator.permissions) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
        if (permissionStatus.state === "denied") {
          toast.error("Location blocked. Check browser settings or try IP location.", { id: "gps-request", duration: 5000 });
          await fallbackToIPGeolocation();
          return;
        }
      } catch (error) {
        console.error("Geolocation permission check failed:", error.message);
      }
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        let gpsLocation;
        try {
          const response = await axios.get(`${API}/weather/reverse`, { params: { lat: latitude, lon: longitude } });
          const locationName = response.data.name || "Your Location";
          const state = response.data.state ? `, ${response.data.state}` : "";
          const country = response.data.country ? `, ${response.data.country}` : "";
          gpsLocation = { lat: latitude, lon: longitude, name: `${locationName}${state}${country}` };
          toast.success(`GPS Location: ${locationName}${state}`, { id: "gps-request" });
        } catch {
          gpsLocation = { lat: latitude, lon: longitude, name: "Your Location" };
          toast.success("GPS location acquired!", { id: "gps-request" });
        }
        applyLocation(gpsLocation);
        await persistDefaultLocation(gpsLocation);
      },
      async (error) => {
        if (error.code === 1) toast.loading("GPS denied, trying IP location...", { id: "gps-request" });
        else if (error.code === 2) toast.error("Location unavailable. Trying IP location...", { id: "gps-request" });
        else if (error.code === 3) toast.error("Location timed out. Trying IP location...", { id: "gps-request" });
        else toast.error("Could not get GPS location", { id: "gps-request" });
        await fallbackToIPGeolocation();
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, [applyLocation, fallbackToIPGeolocation, persistDefaultLocation]);

  const handleSearch = useCallback(async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/weather/search`, { params: { q: query } });
      setSearchResults(response.data);
    } catch {
      toast.error("Search failed");
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(debounce);
  }, [searchQuery, handleSearch]);

  const selectLocation = useCallback(async (loc) => {
    const newLocation = { lat: loc.lat, lon: loc.lon, name: formatLocationName(loc) };
    applyLocation(newLocation, { clearSearch: true, clearSummary: true });
    await persistDefaultLocation(newLocation);
  }, [applyLocation, persistDefaultLocation]);

  const saveLocation = useCallback(async () => {
    try {
      await axios.post(`${API}/locations`, { name: location.name, lat: location.lat, lon: location.lon });
      fetchSavedLocations();
      toast.success("Location saved!");
    } catch {
      toast.error("Failed to save location");
    }
  }, [fetchSavedLocations, location]);

  const removeLocation = useCallback(async (id) => {
    try {
      await axios.delete(`${API}/locations/${id}`);
      fetchSavedLocations();
      toast.success("Location removed");
    } catch {
      toast.error("Failed to remove location");
    }
  }, [fetchSavedLocations]);

  useEffect(() => {
    getUserLocation();
    fetchSavedLocations();
  }, [getUserLocation, fetchSavedLocations]);

  const isLocationSaved = location && savedLocations.some(
    (loc) => Math.abs(loc.lat - location.lat) < 0.01 && Math.abs(loc.lon - location.lon) < 0.01
  );

  return {
    gettingLocation,
    isLocationSaved,
    isSearching,
    location,
    removeLocation,
    requestGPSLocation,
    saveLocation,
    savedLocations,
    searchQuery,
    searchResults,
    selectLocation,
    setLocation,
    setSearchQuery,
  };
}